import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { Box, IconButton, TableCell, TableHead, TableRow } from '@mui/material';
import React, { useMemo } from 'react';
import { SortMenuOption } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { StyledTableCellHead } from 'src/components/table/CustomTable.styled';
import { CustomTableWithPagination } from 'src/components/table/CustomTableWithPagination/CustomTableWithPagination';
import { ControlType } from 'src/components/table/CustomTableWithPagination/TableControlsHeader/TableControlsHeader.interface';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { MAX_ROWS_PER_PAGE } from 'src/constants';
import { ILocationListing } from 'src/interfaces';
import { lang } from 'src/utils';

import { getLocationColumns } from './locationColumns.helper';
import { DisabledLocationCheckbox } from './LocationListTable.styled';

interface LocationListTableProps {
  rows: ILocationListing[];
  loading: boolean;
  selectedSortOption?: SortMenuOption;
  onSortChange?: (option: SortMenuOption) => void;
  page: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (event: React.ChangeEvent<unknown>, value: number) => void;
  rowsPerPage?: number;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  renderActions?: (row: ILocationListing) => React.ReactNode;
  onRowClick?: (row: ILocationListing) => void;
  renderTags: (row: ILocationListing) => React.ReactNode;
  getBannerChecked?: (row: ILocationListing) => boolean;
  getMessagingChecked?: (row: ILocationListing) => boolean;
  itemsToShowCheckbox?: ILocationListing[];
  bodyBackgroundColor?: string;
}

export const LocationListTable: React.FC<LocationListTableProps> = ({
  rows,
  loading,
  selectedSortOption,
  onSortChange,
  page,
  totalPages,
  totalCount,
  onPageChange,
  rowsPerPage,
  itemsToShowCheckbox,
  renderActions,
  onRowClick,
  renderTags,
  getBannerChecked,
  getMessagingChecked,
  onRowsPerPageChange,
  bodyBackgroundColor,
}) => {
  const columns = useMemo(() => getLocationColumns(), []);

  const bannerChecked = getBannerChecked || (() => false);
  const messagingChecked = getMessagingChecked || (() => true);

  return (
    <CustomTableWithPagination
      columns={columns}
      rows={rows}
      loading={loading}
      onSortChange={onSortChange}
      selectedSortOption={selectedSortOption}
      page={page}
      totalPages={totalPages}
      totalCount={totalCount}
      onPageChange={onPageChange}
      rowsPerPage={rowsPerPage ?? MAX_ROWS_PER_PAGE}
      onRowsPerPageChange={onRowsPerPageChange}
      showTableControls
      visibleControls={[ControlType.ITEMS_PER_PAGE, ControlType.SORT]}
      onRowClick={onRowClick}
      bodyBackgroundColor={bodyBackgroundColor}
      tableHeadRenderer={(tableColumns) => (
        <TableHead>
          <TableRow>
            {tableColumns.map((col, idx) => (
              <StyledTableCellHead
                key={idx}
                align={col.align || 'left'}
                sx={{
                  width: col.width,
                  pl:
                    col.id === 'banner' || col.id === 'messaging'
                      ? 0
                      : undefined,
                }}>
                {col.id === 'messaging' ? (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {col.label}
                    <CustomTooltip
                      title={lang.rmhcToolTip}
                      placement="top"
                      skipValidateOverflow={true}>
                      <IconButton
                        disableRipple
                        size="small"
                        sx={{
                          padding: 0,
                          '&:hover': { backgroundColor: 'transparent' },
                        }}>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                ) : col.id === 'banner' ? (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {col.label}
                    <CustomTooltip
                      title={lang.bannerTooltip}
                      placement="top"
                      skipValidateOverflow={true}>
                      <IconButton
                        disableRipple
                        size="small"
                        sx={{
                          padding: 0,
                          '&:hover': { backgroundColor: 'transparent' },
                        }}>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                ) : (
                  col.label || ''
                )}
              </StyledTableCellHead>
            ))}
          </TableRow>
        </TableHead>
      )}
      customCellRenderer={{
        banner: (row: ILocationListing) =>
          itemsToShowCheckbox?.find(
            (item) => item.location_code === row.location_code,
          ) ? (
            <TableCell key="banner" align="left" sx={{ pl: 0 }}>
              <DisabledLocationCheckbox checked={bannerChecked(row)} disabled />
            </TableCell>
          ) : (
            <TableCell key="banner" />
          ),
        messaging: (row: ILocationListing) =>
          itemsToShowCheckbox?.find(
            (item) => item.location_code === row.location_code,
          ) ? (
            <TableCell key="messaging" align="left" sx={{ pl: 0 }}>
              <DisabledLocationCheckbox
                checked={messagingChecked(row)}
                disabled
              />
            </TableCell>
          ) : (
            <TableCell key="messaging" />
          ),
      }}
      renderActions={renderActions}
      renderTags={renderTags}
    />
  );
};
